import React, { useState } from 'react';
import OverflowMenu from './OverflowMenu';
import Button from './Button';
import './DataTable.css';

/* Chevron de fila expandible (F3). */
function ExpandChevron({ open }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className={`ui-dt-expand-icon${open ? ' is-open' : ''}`} aria-hidden="true">
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

/* Tabla administrativa global (F2, ESTANDAR §7/§7.1/§8.1 + brief §9).
   · Encabezado sticky, filas 48px (variant='rich': 80px para catálogo/inventario).
   · Cifras a la derecha con tabular-nums (align:'right' en la columna).
   · Estados: carga (skeleton), error (+reintentar), vacío (compacto, con acción),
     sin-resultados-por-filtros (ofrece limpiar).
   · Fila seleccionada: tinte violeta suave + borde lateral (para el inspector).
   · rowActions(row) agrega la columna ⋮ (OverflowMenu) — SIEMPRE hay un control
     explícito además del click en la fila (§7.1).
   · Paginación: 'loadMore' es EL estándar (§8.1) con bloques fijos de 30
     filas — la página de Pedidos (F4.5: el cálculo dinámico por alto murió,
     quedaba corto). 'paged' sobrevive solo para casos legados.
   · Ordenamiento CONTROLADO: sort={columnId,dir} + onSortChange (el caller
     decide si ordena en memoria o re-consulta). */

/* (F4.5: useAutoPageSize murió — el bloque de "Cargar más" es FIJO de 30
   filas, la página de Pedidos. El cálculo por alto disponible quedaba corto
   — "carga inicial muy corta", hallazgo de Miguel — y 30 llena cualquier
   viewport; el scroll interno de la tabla absorbe el resto.) */

function SortArrow({ dir }) {
  return <span className="ui-dt-sort" aria-hidden="true">{dir === 'asc' ? '▲' : '▼'}</span>;
}

export default function DataTable({
  columns = [],             // [{ id, header, render(row), align?, width?, sortable?, hideSm? }]
                            // hideSm (KIT CRECE, pasada móvil F3): la columna se oculta
                            // <768px — prioridad de columnas en vez de scroll horizontal
                            // kilométrico; lo oculto sigue visible en la fila expandida
                            // y en el inspector.
  rows = [],
  rowKey = '_id',
  loading = false,
  error = null,             // string → estado de error
  onRetry = null,
  empty = null,             // { icon, title, description, action } (ESTANDAR §10, compacto)
  hasActiveFilters = false, // true + 0 filas → "sin coincidencias" + limpiar
  onClearFilters = null,
  onRowClick = null,
  selectedKey = null,
  rowActions = null,        // (row) => items de OverflowMenu
  rowActionsHideSm = false, // móvil: oculta la columna ⋮ — SOLO cuando todas sus
                            // acciones también viven en el inspector (la fila
                            // tocable es el control explícito en táctil)
  variant = 'standard',     // 'standard' 48px | 'rich' 80px
  sort = null,              // { columnId, dir: 'asc'|'desc' }
  onSortChange = null,
  pagination = null,        // { mode:'paged', page, pageSize, total, onPageChange } | { mode:'loadMore', hasMore, loadingMore, onLoadMore }
  skeletonRows = 6,
  fit = null,               // true: la tabla abraza su contenido (alineada a la izquierda).
                            // null (default): automático con ≤3 columnas — una tabla corta
                            // NO estira sus columnas a los bordes (corrección Miguel 2026-07-22)
  renderExpanded = null,    // (row) => nodo BREVE (productos, desglose de pago — brief §9;
                            // nunca formularios). Agrega columna de chevron; convive con el
                            // inspector: chevron = vistazo rápido, click en la fila = detalle.
  groupBy = null,           // (row) => string | { label, meta } — fila de encabezado de grupo
                            // cuando el valor cambia entre filas consecutivas (el caller ordena
                            // por el grupo). KIT CRECE F4: conserva la agrupación por categoría
                            // de Inventario sin que el módulo invente su propia tabla.
  className = '',
}) {
  const keyOf = (row, i) => (typeof rowKey === 'function' ? rowKey(row) : row[rowKey]) ?? i;
  const esFit = fit ?? (columns.length + (rowActions ? 1 : 0) <= 3);
  const [expandedKey, setExpandedKey] = useState(null);

  const clickSort = (col) => {
    if (!col.sortable || !onSortChange) return;
    const dir = sort?.columnId === col.id && sort.dir === 'asc' ? 'desc' : 'asc';
    onSortChange({ columnId: col.id, dir });
  };

  const totalCols = columns.length + (rowActions ? 1 : 0) + (renderExpanded ? 1 : 0);
  const sinFilas = !loading && !error && rows.length === 0;

  return (
    <div className={`ui-dt ui-dt--${variant}${esFit ? ' ui-dt--fit' : ''}${className ? ` ${className}` : ''}`}>
      <div className="ui-dt-scroll">
        <table>
          <thead>
            <tr>
              {renderExpanded && <th className="ui-dt-expand-col" aria-label="Expandir" />}
              {columns.map((c) => (
                <th
                  key={c.id}
                  style={c.width ? { width: c.width } : undefined}
                  className={`${c.align ? `ui-dt-align-${c.align}` : ''}${c.sortable && onSortChange ? ' ui-dt-sortable' : ''}${c.hideSm ? ' ui-dt-hide-sm' : ''}`}
                  aria-sort={sort?.columnId === c.id ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  onClick={() => clickSort(c)}
                >
                  {c.header}
                  {sort?.columnId === c.id && <SortArrow dir={sort.dir} />}
                </th>
              ))}
              {rowActions && <th className={`ui-dt-actions-col${rowActionsHideSm ? ' ui-dt-hide-sm' : ''}`} aria-label="Acciones" />}
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: skeletonRows }, (_, i) => (
              <tr key={`sk-${i}`} className="ui-dt-skeleton-row" aria-hidden="true">
                {columns.map((c) => <td key={c.id}><span className="ui-dt-skeleton" /></td>)}
                {rowActions && <td />}
              </tr>
            ))}

            {!loading && error && (
              <tr><td colSpan={totalCols}>
                <div className="ui-dt-state ui-dt-state--error" role="alert">
                  <span>{error}</span>
                  {onRetry && <Button size="sm" onClick={onRetry}>Reintentar</Button>}
                </div>
              </td></tr>
            )}

            {sinFilas && hasActiveFilters && (
              <tr><td colSpan={totalCols}>
                <div className="ui-dt-state">
                  <span>No hay coincidencias con los filtros aplicados.</span>
                  {onClearFilters && <Button variant="tertiary" size="sm" onClick={onClearFilters}>Limpiar filtros</Button>}
                </div>
              </td></tr>
            )}

            {sinFilas && !hasActiveFilters && (
              <tr><td colSpan={totalCols}>
                <div className="ui-dt-state">
                  {empty?.icon && <span className="ui-dt-empty-icon" aria-hidden="true">{empty.icon}</span>}
                  <div className="ui-dt-empty-copy">
                    <strong>{empty?.title || 'Aún no hay datos'}</strong>
                    {empty?.description && <span>{empty.description}</span>}
                  </div>
                  {empty?.action}
                </div>
              </td></tr>
            )}

            {!loading && !error && rows.map((row, i) => {
              const k = keyOf(row, i);
              const isSelected = selectedKey != null && k === selectedKey;
              const isExpanded = renderExpanded && expandedKey === k;
              const grupo = groupBy ? groupBy(row) : null;
              const grupoLabel = grupo && (typeof grupo === 'string' ? grupo : grupo.label);
              const grupoPrev = groupBy && i > 0 ? groupBy(rows[i - 1]) : null;
              const grupoPrevLabel = grupoPrev && (typeof grupoPrev === 'string' ? grupoPrev : grupoPrev.label);
              const abreGrupo = grupoLabel != null && grupoLabel !== grupoPrevLabel;
              return (
                <React.Fragment key={k}>
                  {abreGrupo && (
                    <tr className="ui-dt-group-row" aria-hidden="true">
                      <td colSpan={totalCols}>
                        <span className="ui-dt-group-label">{grupoLabel}</span>
                        {typeof grupo === 'object' && grupo.meta && <small className="ui-dt-group-meta">{grupo.meta}</small>}
                      </td>
                    </tr>
                  )}
                  <tr
                    className={`${onRowClick ? 'ui-dt-clickable' : ''}${isSelected ? ' ui-dt-selected' : ''}`}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    aria-selected={isSelected || undefined}
                  >
                    {renderExpanded && (
                      <td className="ui-dt-expand-col">
                        <button
                          type="button"
                          className="ui-dt-expand-btn"
                          aria-label={isExpanded ? 'Contraer' : 'Expandir'}
                          aria-expanded={isExpanded}
                          onClick={(e) => { e.stopPropagation(); setExpandedKey(isExpanded ? null : k); }}
                        >
                          <ExpandChevron open={isExpanded} />
                        </button>
                      </td>
                    )}
                    {columns.map((c) => (
                      <td key={c.id} className={`${c.align ? `ui-dt-align-${c.align}` : ''}${c.hideSm ? ' ui-dt-hide-sm' : ''}`}>
                        {c.render ? c.render(row) : row[c.id]}
                      </td>
                    ))}
                    {rowActions && (
                      <td className={`ui-dt-actions-col${rowActionsHideSm ? ' ui-dt-hide-sm' : ''}`}>
                        <OverflowMenu items={rowActions(row) || []} />
                      </td>
                    )}
                  </tr>
                  {isExpanded && (
                    <tr className="ui-dt-expanded-row">
                      <td colSpan={totalCols}>{renderExpanded(row)}</td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {pagination?.mode === 'paged' && !loading && !error && pagination.total > pagination.pageSize && (
        <div className="ui-dt-pager">
          <span className="ui-dt-pager-info">
            {((pagination.page - 1) * pagination.pageSize + 1).toLocaleString('es-CO')}–{Math.min(pagination.page * pagination.pageSize, pagination.total).toLocaleString('es-CO')} de {pagination.total.toLocaleString('es-CO')}
          </span>
          <Button size="sm" disabled={pagination.page <= 1} onClick={() => pagination.onPageChange(pagination.page - 1)}>Anterior</Button>
          <Button size="sm" disabled={pagination.page * pagination.pageSize >= pagination.total} onClick={() => pagination.onPageChange(pagination.page + 1)}>Siguiente</Button>
        </div>
      )}

      {pagination?.mode === 'loadMore' && !loading && !error && pagination.hasMore && (
        <div className="ui-dt-pager ui-dt-pager--center">
          <Button size="sm" loading={pagination.loadingMore} onClick={pagination.onLoadMore}>Cargar más</Button>
        </div>
      )}
    </div>
  );
}
